/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useMemo, useEffect } from "react";
import AuthAPI from "../../../api/firebase/AuthAPI";
import OrdersAPI from "../../../api/firebase/OrdersAPI";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Fade,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import CustomDataGrid from "../../../components/common/DataGrid/CustomDataGrid";
import AdminStatCard from "../../../components/admin/stat-card/AdminStatCard";
import { formatDateTimeForAdmin } from "../../../utils/dateUtils";
import { styles } from "./styles";

// Enhanced Icons
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearIcon from "@mui/icons-material/Clear";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const parseTimestamp = (value) => {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const t = new Date(value).getTime();
    return isNaN(t) ? 0 : t;
  }
  return 0;
};

// Only consider an order valid for buyer determination if status is not created or cancelled
const isValidBuyerOrder = (order) => {
  if (!order) return false;
  if (typeof order === "string") return true;
  const status = (order.status || "").toLowerCase().trim();
  if (status === "created") return false;
  if (status.includes("cancel")) return false;
  return true;
};

const isUserAdmin = (user) => {
  if (!user || !user.role) return false;
  if (Array.isArray(user.role)) {
    return user.role.some((r) => String(r).toLowerCase().includes("admin"));
  }
  if (typeof user.role === "string") {
    return user.role.toLowerCase().includes("admin");
  }
  return false;
};

const AllUsers = () => {
  const [usersArr, setUsersArr] = useState([]);
  const [ordersArr, setOrdersArr] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL"); // ALL, BUYER, ADMIN, USER, RECENT

  // Selected User Modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [openUserModal, setOpenUserModal] = useState(false);

  // Role change confirmation dialog
  const [roleConfirmDialog, setRoleConfirmDialog] = useState(null); // { user, newIsAdmin }

  const authAPI = useMemo(() => new AuthAPI(), []);
  const ordersAPI = useMemo(() => new OrdersAPI(), []);

  // Snackbar notifications
  const [snackbarState, setSnackbarState] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "success" });

  const showSnackbar = (text, type = "success") => {
    setMessage({ text, type });
    setSnackbarState(true);
  };

  const hideSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarState(false);
  };

  // Fetch all users and orders to identify actual buyers
  const fetchData = async () => {
    setIsRefreshing(true);
    try {
      const [resUsers, resOrders] = await Promise.all([
        authAPI.getAllUsers(),
        ordersAPI.getOrders().catch((err) => {
          console.warn("Could not fetch orders for users page:", err);
          return { success: false, data: [] };
        }),
      ]);

      if (resUsers && resUsers.data) {
        setUsersArr(resUsers.data);
      }
      if (resOrders && resOrders.data) {
        setOrdersArr(resOrders.data);
      }
    } catch (err) {
      console.error("Error fetching users/orders data:", err);
      showSnackbar("Failed to refresh users data.", "error");
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Map orders by user ID and email for instant O(1) buyer lookups & last purchased date
  const {
    orderCountByUserId,
    orderCountByUserEmail,
    lastPurchaseByUserId,
    lastPurchaseByUserEmail,
  } = useMemo(() => {
    const byId = new Map();
    const byEmail = new Map();
    const lastDateById = new Map();
    const lastDateByEmail = new Map();

    ordersArr.forEach((order) => {
      // Exclude orders with status created or cancelled
      if (!isValidBuyerOrder(order)) return;

      const orderTime = parseTimestamp(order.createdAt || order.modifiedAt);
      const uid = order.user?.id || order.userId;
      if (uid) {
        const strUid = String(uid);
        byId.set(strUid, (byId.get(strUid) || 0) + 1);
        if (orderTime > (lastDateById.get(strUid) || 0)) {
          lastDateById.set(strUid, orderTime);
        }
      }
      const email = (
        order.user?.email ||
        order.customerEmail ||
        order.email ||
        ""
      ).toLowerCase().trim();
      if (email) {
        byEmail.set(email, (byEmail.get(email) || 0) + 1);
        if (orderTime > (lastDateByEmail.get(email) || 0)) {
          lastDateByEmail.set(email, orderTime);
        }
      }
    });

    return {
      orderCountByUserId: byId,
      orderCountByUserEmail: byEmail,
      lastPurchaseByUserId: lastDateById,
      lastPurchaseByUserEmail: lastDateByEmail,
    };
  }, [ordersArr]);

  // Compute order count for any user (excluding created/cancelled orders)
  const getUserOrderCount = (user) => {
    if (!user) return 0;
    let fromUserOrders = 0;
    if (Array.isArray(user.orders) && user.orders.length > 0) {
      fromUserOrders = user.orders.filter(isValidBuyerOrder).length;
    }
    const uid = user.id ? String(user.id) : "";
    const email = (user.email || "").toLowerCase().trim();
    const fromOrdersById = uid ? (orderCountByUserId.get(uid) || 0) : 0;
    const fromOrdersByEmail = email ? (orderCountByUserEmail.get(email) || 0) : 0;
    return Math.max(fromUserOrders, fromOrdersById, fromOrdersByEmail);
  };

  // Get last purchased timestamp for any user (excluding created/cancelled orders)
  const getUserLastPurchaseTime = (user) => {
    if (!user) return 0;
    let latestTime = 0;

    // Check user.orders array (only valid buyer orders)
    if (Array.isArray(user.orders) && user.orders.length > 0) {
      user.orders.forEach((o) => {
        if (!isValidBuyerOrder(o)) return;
        const t = parseTimestamp(o.createdAt || o.orderDate || o.modifiedAt || o.date);
        if (t > latestTime) latestTime = t;
      });
    }

    const uid = user.id ? String(user.id) : "";
    const email = (user.email || "").toLowerCase().trim();
    const fromOrdersById = uid ? (lastPurchaseByUserId.get(uid) || 0) : 0;
    const fromOrdersByEmail = email ? (lastPurchaseByUserEmail.get(email) || 0) : 0;

    return Math.max(latestTime, fromOrdersById, fromOrdersByEmail);
  };

  // An actual buyer is a user who has placed at least 1 order
  const isActualBuyer = (user) => {
    return getUserOrderCount(user) > 0;
  };

  // KPI Calculations
  const adminCount = useMemo(() => {
    return usersArr.filter((u) => isUserAdmin(u)).length;
  }, [usersArr]);

  const buyersCount = useMemo(() => {
    return usersArr.filter((u) => isActualBuyer(u)).length;
  }, [usersArr, orderCountByUserId, orderCountByUserEmail]);

  const recentCount = useMemo(() => {
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return usersArr.filter((u) => {
      if (!u.createdAt) return false;
      let time = 0;
      if (typeof u.createdAt === "string") {
        time = new Date(u.createdAt).getTime();
      } else if (u.createdAt.seconds) {
        time = u.createdAt.seconds * 1000;
      } else if (u.createdAt.toDate) {
        time = u.createdAt.toDate().getTime();
      }
      return time >= thirtyDaysAgo;
    }).length;
  }, [usersArr]);

  // Filtered Users
  const filteredUsers = useMemo(() => {
    return usersArr.filter((user) => {
      // 1. Text Search
      if (searchQuery.trim()) {
        const query = searchQuery.trim().toLowerCase();
        const matches = [
          user.displayName,
          user.email,
          user.id,
          user.phoneNumber,
          user.mobile,
          isUserAdmin(user) ? "admin administrator" : "user customer",
          isActualBuyer(user) ? "buyer actual buyer order orders" : "",
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query);

        if (!matches) return false;
      }

      // 2. Role / Status Filter
      if (roleFilter === "ADMIN" && !isUserAdmin(user)) return false;
      if (roleFilter === "BUYER" && !isActualBuyer(user)) return false;
      if (roleFilter === "USER" && isUserAdmin(user)) return false;
      if (roleFilter === "RECENT") {
        const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
        let time = 0;
        if (typeof user.createdAt === "string") {
          time = new Date(user.createdAt).getTime();
        } else if (user.createdAt?.seconds) {
          time = user.createdAt.seconds * 1000;
        } else if (user.createdAt?.toDate) {
          time = user.createdAt.toDate().getTime();
        }
        if (time < thirtyDaysAgo) return false;
      }

      return true;
    });
  }, [usersArr, searchQuery, roleFilter, orderCountByUserId, orderCountByUserEmail]);

  const handleResetFilters = () => {
    setSearchQuery("");
    setRoleFilter("ALL");
  };

  const hasActiveFilters = searchQuery.trim() !== "" || roleFilter !== "ALL";

  // Update user role
  const handleExecuteRoleChange = async () => {
    if (!roleConfirmDialog) return;
    const { user, newIsAdmin } = roleConfirmDialog;
    const updatedRole = newIsAdmin ? ["Admin", "User"] : ["User"];

    try {
      await authAPI.setUserInTable({
        id: user.id,
        payload: {
          role: updatedRole,
        },
      });

      const updated = usersArr.map((u) =>
        u.id === user.id ? { ...u, role: updatedRole } : u
      );
      setUsersArr(updated);

      if (selectedUser?.id === user.id) {
        setSelectedUser({ ...selectedUser, role: updatedRole });
      }

      showSnackbar(
        `Role for "${user.displayName || user.email}" updated to ${
          newIsAdmin ? "Administrator" : "Customer"
        }!`,
        "success"
      );
    } catch (err) {
      console.error("Error updating user role:", err);
      showSnackbar("Failed to update user role. Please try again.", "error");
    } finally {
      setRoleConfirmDialog(null);
    }
  };

  // Trigger password reset email
  const handleSendPasswordReset = async (email) => {
    if (!email) return;
    try {
      await authAPI.sendResetPasswordLinkOnEmail(email);
      showSnackbar(`Password reset link sent to ${email}`, "success");
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to send password reset email.", "error");
    }
  };

  // Open User Details Modal
  const handleOpenUserModal = (user) => {
    setSelectedUser(user);
    setOpenUserModal(true);
  };

  const handleCloseUserModal = () => {
    setOpenUserModal(false);
  };

  // Columns for Users DataGrid
  const columns = useMemo(
    () => [
      {
        field: "photoURL",
        headerName: "User",
        width: 75,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const photo = params.row.photoURL;
          const name = params.row.displayName || params.row.email || "U";
          const firstChar = name.charAt(0).toUpperCase();
          return (
            <Avatar
              src={photo || ""}
              alt={name}
              sx={{
                width: 38,
                height: 38,
                borderRadius: "10px",
                border: "1px solid #e2e8f0",
                bgcolor: isUserAdmin(params.row) ? "rgba(109, 40, 217, 0.1)" : "rgba(0, 77, 64, 0.08)",
                color: isUserAdmin(params.row) ? "#6d28d9" : "#004d40",
                fontWeight: 800,
                fontSize: "0.95rem",
              }}
            >
              {firstChar}
            </Avatar>
          );
        },
      },
      {
        field: "id",
        headerName: "ID",
        hide: true,
      },
      {
        field: "displayName",
        headerName: "Name",
        flex: 1,
        minWidth: 200,
        renderCell: (params) => {
          const name = params.row.displayName || "Customer";
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.2, overflow: "hidden" }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: "#1e293b",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {name}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: "ui-monospace, monospace",
                  fontSize: "0.7rem",
                  color: "#64748b",
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                #{params.row.id}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "email",
        headerName: "Email Address",
        flex: 1,
        minWidth: 240,
        renderCell: (params) => {
          const email = params.row.email || "No email registered";
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, overflow: "hidden" }}>
              <MailOutlineRoundedIcon sx={{ fontSize: 16, color: "#94a3b8", flexShrink: 0 }} />
              <Typography
                variant="body2"
                sx={{
                  color: "#334155",
                  fontWeight: 500,
                  fontSize: "0.85rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {email}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "role",
        headerName: "Role / Access",
        width: 170,
        renderCell: (params) => {
          const isAdmin = isUserAdmin(params.row);
          return (
            <Chip
              icon={
                isAdmin ? (
                  <AdminPanelSettingsRoundedIcon sx={{ fontSize: "14px !important" }} />
                ) : (
                  <PersonOutlineRoundedIcon sx={{ fontSize: "14px !important" }} />
                )
              }
              label={isAdmin ? "Administrator" : "Customer"}
              size="small"
              sx={{
                height: 24,
                fontSize: "0.75rem",
                fontWeight: 700,
                bgcolor: isAdmin ? "#f5f3ff" : "#ecfdf5",
                color: isAdmin ? "#6d28d9" : "#047857",
                border: `1px solid ${isAdmin ? "#ddd6fe" : "#a7f3d0"}`,
                "& .MuiChip-icon": {
                  color: isAdmin ? "#6d28d9" : "#047857",
                },
              }}
            />
          );
        },
      },
      {
        field: "orders",
        headerName: "Orders",
        width: 150,
        valueGetter: (params) => getUserOrderCount(params.row),
        renderCell: (params) => {
          const count = getUserOrderCount(params.row);
          if (count > 0) {
            return (
              <Chip
                icon={<ShoppingCartOutlinedIcon sx={{ fontSize: "13px !important" }} />}
                label={`${count} ${count === 1 ? "Order" : "Orders"}`}
                size="small"
                sx={{
                  height: 24,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  bgcolor: "#ecfdf5",
                  color: "#047857",
                  border: "1px solid #a7f3d0",
                  "& .MuiChip-icon": {
                    color: "#047857",
                  },
                }}
              />
            );
          }
          return (
            <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 500 }}>
              0 orders
            </Typography>
          );
        },
      },
      {
        field: "lastPurchased",
        headerName: "Last Purchased",
        width: 185,
        valueGetter: (params) => {
          const time = getUserLastPurchaseTime(params.row);
          return time ? new Date(time) : new Date(0);
        },
        renderCell: (params) => {
          const time = getUserLastPurchaseTime(params.row);
          if (time > 0) {
            const formatted = formatDateTimeForAdmin(new Date(time));
            return (
              <Typography variant="caption" sx={{ color: "#0f172a", fontWeight: 600 }}>
                {formatted}
              </Typography>
            );
          }
          return (
            <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 500 }}>
              Never
            </Typography>
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Joined Date",
        width: 180,
        valueGetter: (params) => {
          const createdAt = params.row.createdAt;
          if (typeof createdAt === "string") {
            const date = new Date(createdAt);
            return isNaN(date.getTime()) ? new Date(0) : date;
          } else if (createdAt && createdAt.seconds) {
            return new Date(createdAt.seconds * 1000);
          } else if (createdAt && createdAt.toDate) {
            return createdAt.toDate();
          }
          return new Date(0);
        },
        renderCell: (params) => {
          const t = formatDateTimeForAdmin(params.row.createdAt);
          return (
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
              {t}
            </Typography>
          );
        },
      },
    ],
    [orderCountByUserId, orderCountByUserEmail, lastPurchaseByUserId, lastPurchaseByUserEmail]
  );

  return (
    <Box sx={{ pb: 4 }}>
      {/* Hero Header matching Products & Orders */}
      <Box
        sx={{
          mb: 3,
          p: { xs: 2.5, sm: 3.5 },
          borderRadius: 4,
          color: "#ffffff",
          background: "linear-gradient(135deg, #00382e 0%, #004d40 60%, #056152 100%)",
          boxShadow: "0 10px 25px -5px rgba(0, 77, 64, 0.3)",
          display: { xs: "none", sm: "flex" },
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.75rem", sm: "2.2rem" },
              letterSpacing: "-0.02em",
            }}
          >
            Users
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.88, mt: 0.5, maxWidth: 560 }}>
            Manage registered customer accounts, grant administrator privileges, oversee authentication, and manage permissions.
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ zIndex: 1, alignSelf: { xs: "stretch", sm: "auto" } }}
        >
          <Tooltip title="Refresh Users Data">
            <IconButton
              onClick={fetchData}
              disabled={isRefreshing}
              sx={{
                bgcolor: "rgba(255,255,255,0.15)",
                color: "#ffffff",
                "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
              }}
            >
              <RefreshIcon
                sx={{
                  animation: isRefreshing ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
            </IconButton>
          </Tooltip>

          <Chip
            icon={<PeopleAltOutlinedIcon sx={{ color: "#004d40 !important", fontSize: 16 }} />}
            label={`Total: ${usersArr.length} Accounts`}
            sx={{
              fontWeight: 800,
              bgcolor: "#ffffff",
              color: "#004d40",
              px: 1.5,
              py: 2.2,
              borderRadius: "10px",
              boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
              fontSize: "0.875rem",
            }}
          />
        </Stack>
      </Box>

      {/* KPI Summary Cards */}
      <Grid2 container spacing={{ xs: 1.25, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        {/* Total Users */}
        <Grid2 xs={6} sm={6} md={3}>
          <AdminStatCard
            label="Total Accounts"
            value={usersArr.length}
            note={`${buyersCount} Actual Buyers • ${adminCount} Admins`}
            icon={<PeopleAltOutlinedIcon />}
            onClick={handleResetFilters}
          />
        </Grid2>

        {/* Administrators */}
        <Grid2 xs={6} sm={6} md={3}>
          <AdminStatCard
            label="Administrators"
            value={adminCount}
            note="Full back-office system access"
            icon={<AdminPanelSettingsRoundedIcon />}
            color="#6d28d9"
            background="#f5f3ff"
            selected={roleFilter === "ADMIN"}
            onClick={() => setRoleFilter(roleFilter === "ADMIN" ? "ALL" : "ADMIN")}
            delay={70}
          />
        </Grid2>

        {/* Actual Buyers */}
        <Grid2 xs={6} sm={6} md={3}>
          <AdminStatCard
            label="Actual Buyers"
            value={buyersCount}
            note="Confirmed active orders"
            icon={<ShoppingCartOutlinedIcon />}
            color="#047857"
            background="#ecfdf5"
            selected={roleFilter === "BUYER"}
            onClick={() => setRoleFilter(roleFilter === "BUYER" ? "ALL" : "BUYER")}
            delay={140}
          />
        </Grid2>

        {/* Recent Registrations */}
        <Grid2 xs={6} sm={6} md={3}>
          <AdminStatCard
            label="Recent (30 Days)"
            value={recentCount}
            note="New user registrations"
            icon={<AccessTimeRoundedIcon />}
            color="#1d4ed8"
            background="#eff6ff"
            selected={roleFilter === "RECENT"}
            onClick={() => setRoleFilter(roleFilter === "RECENT" ? "ALL" : "RECENT")}
            delay={210}
          />
        </Grid2>
      </Grid2>

      {/* Filter and Search Bar */}
      <Paper elevation={0} sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 3, mb: 2 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
          >
            {/* Search Input */}
            <TextField
              size="small"
              placeholder="Search by name, email, user ID, or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": { bgcolor: "#f8fafc", borderRadius: 2 },
              }}
            />

            {/* Role Filter Dropdown */}
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel id="user-role-filter-label">Access Role</InputLabel>
              <Select
                labelId="user-role-filter-label"
                value={roleFilter}
                label="Access Role"
                onChange={(e) => setRoleFilter(e.target.value)}
                sx={{ borderRadius: 2, bgcolor: "#f8fafc" }}
              >
                <MenuItem value="ALL">All Roles</MenuItem>
                <MenuItem value="BUYER">Actual Buyers Only</MenuItem>
                <MenuItem value="ADMIN">Administrators Only</MenuItem>
                <MenuItem value="USER">Customers (Non-Admin)</MenuItem>
                <MenuItem value="RECENT">Joined in Last 30 Days</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {/* Active Filter Pills Bar */}
          {hasActiveFilters && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b" }}>
                Active Filters:
              </Typography>

              {searchQuery && (
                <Chip
                  size="small"
                  label={`Search: "${searchQuery}"`}
                  onDelete={() => setSearchQuery("")}
                  sx={{ borderRadius: 1.5 }}
                />
              )}

              {roleFilter !== "ALL" && (
                <Chip
                  size="small"
                  label={`Role: ${
                    roleFilter === "ADMIN"
                      ? "Administrators"
                      : roleFilter === "BUYER"
                      ? "Actual Buyers"
                      : roleFilter === "USER"
                      ? "Customers"
                      : "Recent (30 Days)"
                  }`}
                  onDelete={() => setRoleFilter("ALL")}
                  sx={{ borderRadius: 1.5 }}
                />
              )}

              <Chip
                size="small"
                label="Clear all"
                onClick={handleResetFilters}
                color="error"
                variant="outlined"
                sx={{ borderRadius: 1.5, fontWeight: 700 }}
              />

              <Typography variant="caption" sx={{ color: "#94a3b8", ml: "auto !important" }}>
                Showing {filteredUsers.length} of {usersArr.length} accounts
              </Typography>
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Users DataGrid */}
      <CustomDataGrid
        rows={filteredUsers}
        columns={columns}
        styles={{
          height: "640px",
          bgcolor: "#ffffff",
          borderRadius: "16px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.03)",
        }}
        initialState={{
          sorting: {
            sortModel: [{ field: "createdAt", sort: "desc" }],
          },
        }}
        pageSizes={[50, 100]}
        onRowClickHandle={(params) => handleOpenUserModal(params.row)}
      />

      {/* User Details & Role Management Modal */}
      <Modal
        aria-labelledby="user-details-modal-title"
        aria-describedby="user-details-modal-description"
        open={openUserModal}
        onClose={handleCloseUserModal}
        closeAfterTransition
      >
        <Fade in={openUserModal}>
          <Box sx={styles.boxStyle}>
            {selectedUser && (
              <>
                {/* Modal Header */}
                <Box
                  sx={{
                    px: { xs: 2.5, sm: 3 },
                    py: 2.2,
                    borderBottom: "1px solid #f1f5f9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    bgcolor: "#ffffff",
                    flexShrink: 0,
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar
                      src={selectedUser.photoURL || ""}
                      alt={selectedUser.displayName || selectedUser.email || "U"}
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "12px",
                        bgcolor: isUserAdmin(selectedUser)
                          ? "rgba(109, 40, 217, 0.12)"
                          : "rgba(0, 77, 64, 0.1)",
                        color: isUserAdmin(selectedUser) ? "#6d28d9" : "#004d40",
                        fontWeight: 800,
                        fontSize: "1.1rem",
                      }}
                    >
                      {(selectedUser.displayName || selectedUser.email || "U")
                        .charAt(0)
                        .toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 800, lineHeight: 1.2, color: "#1e293b", fontSize: "1.1rem" }}
                      >
                        {selectedUser.displayName || "Customer Profile"}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#64748b" }}>
                        {selectedUser.email || "No email available"}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Tooltip title="Copy User ID">
                      <Chip
                        icon={<ContentCopyIcon sx={{ fontSize: "13px !important" }} />}
                        label={`#${selectedUser.id}`}
                        size="small"
                        onClick={() => {
                          navigator.clipboard?.writeText?.(selectedUser.id);
                          showSnackbar("User ID copied to clipboard!", "success");
                        }}
                        sx={{
                          fontWeight: 700,
                          fontFamily: "ui-monospace, monospace",
                          fontSize: "0.72rem",
                          bgcolor: "#f1f5f9",
                          color: "#475569",
                          cursor: "pointer",
                          display: { xs: "none", sm: "inline-flex" },
                          "&:hover": { bgcolor: "#e2e8f0" },
                        }}
                      />
                    </Tooltip>
                    <IconButton
                      onClick={handleCloseUserModal}
                      size="small"
                      sx={{
                        color: "#64748b",
                        "&:hover": { bgcolor: "#f1f5f9", color: "#1e293b" },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>

                {/* Modal Body */}
                <Box sx={{ p: { xs: 2.5, sm: 3 }, overflowY: "auto", flex: 1 }}>
                  <Stack spacing={2.5}>
                    {/* General Information Card */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        border: "1px solid #e2e8f0",
                        borderRadius: 3,
                        bgcolor: "#ffffff",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 800, color: "#1e293b", mb: 2 }}
                      >
                        Account Details
                      </Typography>

                      <Stack spacing={1.5}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600 }}>
                            Display Name
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                            {selectedUser.displayName || "Not specified"}
                          </Typography>
                        </Box>

                        <Divider sx={{ borderColor: "#f1f5f9" }} />

                        <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600 }}>
                            Email Address
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                            {selectedUser.email || "Not specified"}
                          </Typography>
                        </Box>

                        <Divider sx={{ borderColor: "#f1f5f9" }} />

                        <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600 }}>
                            Registered Since
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                            {formatDateTimeForAdmin(selectedUser.createdAt)}
                          </Typography>
                        </Box>

                        <Divider sx={{ borderColor: "#f1f5f9" }} />

                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5 }}>
                          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600 }}>
                            Purchases / Orders
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {getUserOrderCount(selectedUser) > 0 ? (
                              <Chip
                                icon={<ShoppingCartOutlinedIcon sx={{ fontSize: "13px !important" }} />}
                                label={`${getUserOrderCount(selectedUser)} ${
                                  getUserOrderCount(selectedUser) === 1 ? "Order" : "Orders"
                                } (Actual Buyer)`}
                                size="small"
                                sx={{
                                  height: 24,
                                  fontSize: "0.75rem",
                                  fontWeight: 700,
                                  bgcolor: "#ecfdf5",
                                  color: "#047857",
                                  border: "1px solid #a7f3d0",
                                  "& .MuiChip-icon": { color: "#047857" },
                                }}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 600, color: "#94a3b8" }}>
                                0 Orders (No purchases yet)
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        <Divider sx={{ borderColor: "#f1f5f9" }} />

                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5 }}>
                          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600 }}>
                            Last Purchased Date
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                            {getUserLastPurchaseTime(selectedUser) > 0
                              ? formatDateTimeForAdmin(new Date(getUserLastPurchaseTime(selectedUser)))
                              : "Never (No purchases yet)"}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>

                    {/* Role & Permissions Card */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        border: "1px solid #e2e8f0",
                        borderRadius: 3,
                        bgcolor: "#ffffff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box sx={{ pr: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b" }}>
                            Administrator Privileges
                          </Typography>
                          <Chip
                            label={isUserAdmin(selectedUser) ? "Admin" : "Customer"}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.68rem",
                              fontWeight: 800,
                              bgcolor: isUserAdmin(selectedUser) ? "#f5f3ff" : "#ecfdf5",
                              color: isUserAdmin(selectedUser) ? "#6d28d9" : "#047857",
                              border: `1px solid ${
                                isUserAdmin(selectedUser) ? "#ddd6fe" : "#a7f3d0"
                              }`,
                            }}
                          />
                        </Stack>
                        <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                          Grant full permissions to manage products, categories, orders, and store settings.
                        </Typography>
                      </Box>

                      <Switch
                        checked={isUserAdmin(selectedUser)}
                        onChange={(e) => {
                          setRoleConfirmDialog({
                            user: selectedUser,
                            newIsAdmin: e.target.checked,
                          });
                        }}
                        color="secondary"
                      />
                    </Paper>

                    {/* Security & Password Action Card */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        border: "1px solid #e2e8f0",
                        borderRadius: 3,
                        bgcolor: "#ffffff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b" }}>
                          Password & Security
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                          Send an automated password reset link directly to the customer's email.
                        </Typography>
                      </Box>

                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<LockResetRoundedIcon fontSize="small" />}
                        onClick={() => handleSendPasswordReset(selectedUser.email)}
                        disabled={!selectedUser.email}
                        sx={{
                          borderRadius: "10px",
                          fontWeight: 700,
                          textTransform: "none",
                          borderColor: "#cbd5e1",
                          color: "#475569",
                          "&:hover": {
                            bgcolor: "#f8fafc",
                            borderColor: "#94a3b8",
                          },
                        }}
                      >
                        Reset Password
                      </Button>
                    </Paper>
                  </Stack>
                </Box>

                {/* Modal Footer */}
                <Box
                  sx={{
                    px: { xs: 2.5, sm: 3 },
                    py: 2,
                    borderTop: "1px solid #f1f5f9",
                    bgcolor: "#ffffff",
                    display: "flex",
                    justifyContent: "flex-end",
                    flexShrink: 0,
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleCloseUserModal}
                    sx={{
                      borderRadius: "10px",
                      fontWeight: 700,
                      textTransform: "none",
                      bgcolor: "#004d40",
                      px: 3,
                      py: 0.9,
                      fontSize: "0.875rem",
                      boxShadow: "0 4px 12px rgba(0, 77, 64, 0.2)",
                      "&:hover": { bgcolor: "#00382e" },
                    }}
                  >
                    Done
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Fade>
      </Modal>

      {/* Role Change Confirmation Dialog */}
      <Dialog
        open={Boolean(roleConfirmDialog)}
        onClose={() => setRoleConfirmDialog(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "16px", p: 1 },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: "50%",
              bgcolor: roleConfirmDialog?.newIsAdmin ? "#f5f3ff" : "#fffbeb",
              color: roleConfirmDialog?.newIsAdmin ? "#6d28d9" : "#b45309",
              display: "flex",
            }}
          >
            {roleConfirmDialog?.newIsAdmin ? (
              <AdminPanelSettingsRoundedIcon />
            ) : (
              <WarningAmberRoundedIcon />
            )}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {roleConfirmDialog?.newIsAdmin ? "Grant Admin Access?" : "Revoke Admin Access?"}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <DialogContentText sx={{ color: "#475569", fontSize: "0.95rem" }}>
            Are you sure you want to{" "}
            <strong>
              {roleConfirmDialog?.newIsAdmin ? "grant Administrator privileges to" : "revoke Administrator privileges from"}
            </strong>{" "}
            "{roleConfirmDialog?.user?.displayName || roleConfirmDialog?.user?.email}"?
            {roleConfirmDialog?.newIsAdmin
              ? " This user will have full administrative access to all back-office controls."
              : " This user will become a standard customer."}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1.5, justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            onClick={() => setRoleConfirmDialog(null)}
            sx={{
              borderRadius: "10px",
              borderColor: "#cbd5e1",
              color: "#475569",
              fontWeight: 700,
              textTransform: "none",
              px: 2.5,
              py: 0.9,
              fontSize: "0.875rem",
              transition: "all 0.15s ease",
              "&:hover": {
                borderColor: "#94a3b8",
                bgcolor: "#f8fafc",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleExecuteRoleChange}
            sx={{
              borderRadius: "10px",
              fontWeight: 700,
              textTransform: "none",
              bgcolor: roleConfirmDialog?.newIsAdmin ? "#6d28d9" : "#dc2626",
              px: 2.8,
              py: 0.9,
              fontSize: "0.875rem",
              boxShadow: roleConfirmDialog?.newIsAdmin
                ? "0 2px 8px rgba(109, 40, 217, 0.25)"
                : "0 2px 8px rgba(220, 38, 38, 0.25)",
              transition: "all 0.15s ease",
              "&:hover": {
                bgcolor: roleConfirmDialog?.newIsAdmin ? "#5b21b6" : "#b91c1c",
              },
            }}
          >
            Confirm Change
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbarState}
        autoHideDuration={3500}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={hideSnackbar}
          severity={message.type || "success"}
          sx={{ width: "100%", borderRadius: "10px", fontWeight: 600 }}
        >
          {message.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AllUsers;
